import { getMainLanguage, SupportedLocale, supportedLocales } from "@/i18n";
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { Artwork, ImageSize, tabMenuItems, TabMenuItems } from "./types";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * TipTap ne renvoie jamais une chaîne vide : un éditeur vidé produit
 * `<p class="spacing-paragraph-4"></p>`. Un simple `.trim()` considère donc à
 * tort qu'un champ effacé est encore rempli. On compare le texte visible,
 * balises retirées.
 */
export function isEmptyRichText(html?: string | null): boolean {
  if (!html) return true;
  return html.replace(/<[^>]*>/g, "").replace(/&nbsp;/gi, " ").trim() === "";
}

/** Ramène un contenu TipTap vidé à "" pour qu'il soit vide côté API et en base. */
export function normalizeRichText(html?: string | null): string {
  return isEmptyRichText(html) ? "" : (html as string);
}

export function stripExtension(filename: string): string {
  const lastDot = filename.lastIndexOf(".");
  if (lastDot <= 0) return filename;
  return filename.slice(0, lastDot);
}

export function sortFilesByNameNatural(files: File[]): File[] {
  return [...files].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, {
      numeric: true,
      sensitivity: "base",
    }),
  );
}

export function fileIdentityKey(file: File): string {
  return `${file.name}:${file.size}:${file.lastModified}`;
}

export function slugify(str: string | undefined): string {
  if (!str) return "untitled";
  return str
    .toLowerCase()
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

export async function urlToFile(
  url: string,
  filename: string,
  mimeType: string,
): Promise<File> {
  try {
    const response = await fetch(url, {
      headers: { "Cache-Control": "no-cache" },
    });
    const blob = await response.blob();
    return new File([blob], filename, { type: mimeType });
  } catch (error) {
    console.error("Error converting URL to File:", error);
    throw new Error("Failed to convert URL to File");
  }
}

export function readFile(file: File) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => resolve(reader.result), false);
    reader.readAsDataURL(file);
  });
}

export async function getFileFromUrl(url: string, title?: string) {
  fetch(url as string)
    .then((response) => response.blob())
    .then((blob) => {
      const urlObj = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = urlObj;
      a.download = title ?? "download";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(urlObj);
      a.remove();
    });
}

export function extractHtmlText(s: string) {
  const span = document.createElement("span");
  span.innerHTML = s;
  return span.textContent || span.innerText;
}

export function sanitizeRichTextHtml(html: string): string {
  return html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
    .replace(/on\w+="[^"]*"/g, "")
    .replace(/javascript:[^"']*/g, "")
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, "");
}

export function isRichTextHtml(value: string): boolean {
  return /<\/?(p|ul|ol|li|strong|em|h[1-6]|blockquote|br)\b/i.test(value);
}

/**
 * Named HTML entities map (amp last to avoid double-decode).
 * Covers quotes, punctuation, symbols, letters with accents (French/European).
 */
const HTML_ENTITIES: Record<string, string> = {
  // Quotes (curly and typographic)
  "&lsquo;": "'",
  "&rsquo;": "'",
  "&ldquo;": '"',
  "&rdquo;": '"',
  "&sbquo;": "‚",
  "&bdquo;": "„",
  // Punctuation & spaces
  "&nbsp;": "\u00A0",
  "&mdash;": "—",
  "&ndash;": "–",
  "&hellip;": "…",
  "&bull;": "•",
  "&middot;": "·",
  // Symbols
  "&copy;": "©",
  "&reg;": "®",
  "&trade;": "™",
  "&euro;": "€",
  "&pound;": "£",
  "&cent;": "¢",
  "&deg;": "°",
  "&plusmn;": "±",
  "&frac12;": "½",
  "&frac14;": "¼",
  "&frac34;": "¾",
  "&times;": "×",
  "&divide;": "÷",
  // Letters with accents (lowercase)
  "&agrave;": "à",
  "&aacute;": "á",
  "&acirc;": "â",
  "&auml;": "ä",
  "&aring;": "å",
  "&aelig;": "æ",
  "&ccedil;": "ç",
  "&eacute;": "é",
  "&egrave;": "è",
  "&ecirc;": "ê",
  "&euml;": "ë",
  "&iacute;": "í",
  "&igrave;": "ì",
  "&icirc;": "î",
  "&iuml;": "ï",
  "&ntilde;": "ñ",
  "&oacute;": "ó",
  "&ograve;": "ò",
  "&ocirc;": "ô",
  "&ouml;": "ö",
  "&oslash;": "ø",
  "&oelig;": "œ",
  "&uacute;": "ú",
  "&ugrave;": "ù",
  "&ucirc;": "û",
  "&uuml;": "ü",
  "&yacute;": "ý",
  "&yuml;": "ÿ",
  "&szlig;": "ß",
  // Uppercase
  "&Agrave;": "À",
  "&Aacute;": "Á",
  "&Acirc;": "Â",
  "&Auml;": "Ä",
  "&Aring;": "Å",
  "&AElig;": "Æ",
  "&Ccedil;": "Ç",
  "&Eacute;": "É",
  "&Egrave;": "È",
  "&Ecirc;": "Ê",
  "&Euml;": "Ë",
  "&Iacute;": "Í",
  "&Igrave;": "Ì",
  "&Icirc;": "Î",
  "&Iuml;": "Ï",
  "&Ntilde;": "Ñ",
  "&Oacute;": "Ó",
  "&Ograve;": "Ò",
  "&Ocirc;": "Ô",
  "&Ouml;": "Ö",
  "&Oslash;": "Ø",
  "&OElig;": "Œ",
  "&Uacute;": "Ú",
  "&Ugrave;": "Ù",
  "&Ucirc;": "Û",
  "&Uuml;": "Ü",
  "&Yacute;": "Ý",
  // Basic (must be after longer names that start with these)
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&apos;": "'",
  "&amp;": "&",
};

/**
 * Decode HTML entities and URI components (e.g. &#x27; → ', %27 → ', &amp; → &).
 * Works in any environment (browser + Node). Repeats until stable to handle double-encoding.
 */
export function decodeHtmlEntities(s: string): string {
  if (!s || typeof s !== "string") return s ?? "";
  let r = s;
  try {
    r = decodeURIComponent(r);
  } catch {
    // leave as-is if invalid URI sequence
  }
  const decodeOnePass = (str: string) => {
    let out = str
      .replace(/&#(\d+);?/g, (_, d) => String.fromCharCode(parseInt(d, 10)))
      .replace(/&#x([0-9a-fA-F]+);?/g, (_, h) =>
        String.fromCharCode(parseInt(h, 16)),
      );
    for (const [entity, char] of Object.entries(HTML_ENTITIES)) {
      out = out.split(entity).join(char);
    }
    return out;
  };
  for (let i = 0; i < 5; i++) {
    const next = decodeOnePass(r);
    if (next === r) break;
    r = next;
  }
  return r;
}

// Recursively parse a JSON String.
// If the input is already an object/array, it returns it as is
// If the input is not a string nor an object/array or if the parsing fails, it returns undefined
export function parseJsonOrUndefined<T>(s: unknown): T | undefined {
  if (typeof s === "object" || Array.isArray(s)) return s as T;

  if (typeof s !== "string") return undefined;

  try {
    const firstParse = JSON.parse(s);

    if (typeof firstParse !== "string") return firstParse as T;

    // TECH_DEBT: in the database, some JSON string are encoded with two sets of quotes
    try {
      return JSON.parse(firstParse) as T;
    } catch {
      return firstParse as T;
    }
  } catch {
    return undefined;
  }
}

/**
 * Returns a pretty JSON string when the input is JSON-compatible,
 * otherwise returns a plain string version of the input.
 */
export function parseJsonThenFormatElseString(value: unknown): string {
  if (value == null) return "";

  const parsed = parseJsonOrUndefined<unknown>(value);

  if (parsed !== undefined) {
    if (typeof parsed === "string") return parsed;
    return JSON.stringify(parsed, null, 2);
  }

  return String(value);
}

export const capitalizeFirstLetter = (str: string) =>
  str.charAt(0).toUpperCase() + str.slice(1);

export type WithPartial<T, K extends keyof T> = Omit<T, K> &
  Partial<Pick<T, K>>;

export type Overwrite<A, B> = Omit<A, keyof B> & B;

export const getPaddingToApplyToCenter = (
  index: number,
  sizes: ImageSize[],
  maxWidth: number,
) => {
  const displayHeights = sizes.map(
    (size) => (Math.min(size.width, maxWidth) * size.height) / size.width,
  );
  const maxSize = Math.max(...displayHeights);
  const padding = (maxSize - displayHeights[index]) / 2;
  return padding;
};

export const chunkArray = <T>(array: T[], size: number): T[][] => {
  const result: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
};

export const chunkArrayVertically = <T>(array: T[], size: number): T[][] => {
  const result: T[][] = Array.from({ length: size }, () => []);
  array.forEach((item, index) => {
    result[index % size].push(item);
  });
  return result;
};

export const getImageSize = (url: string): Promise<ImageSize> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = url;

    img.onload = () => {
      resolve({ width: img.width, height: img.height });
    };

    img.onerror = (error) => {
      reject(error);
    };
  });
};

export const filterArrayWithPredicate = <
  T,
  K extends keyof T = {
    [Key in keyof T]: T[Key] extends string ? Key : never;
  }[keyof T],
>(
  array: T[],
  predicate: (value: T[K]) => boolean,
  searchedKeys: K[] = Object.keys(array[0] ?? []) as K[],
) => {
  return array.filter((item) => {
    return searchedKeys.some((key) => predicate(item[key]));
  });
};

export const filterArrayWithSearch = <
  T,
  K extends keyof T = {
    [Key in keyof T]: T[Key] extends string ? Key : never;
  }[keyof T],
>(
  array: T[],
  search: string,
  searchedKeys: K[],
) => {
  if (search.length === 0) return array;
  return filterArrayWithPredicate(
    array,
    (value: T[K]) =>
      typeof value === "string" &&
      value.toLowerCase().includes(search.toLowerCase()),
    searchedKeys,
  );
};

const buildArtworkImageName = (
  artwork: Pick<
    Artwork,
    | "oeuvresDescriptionTitre"
    | "oeuvresDescriptionTechnique"
    | "oeuvresDescriptionSupport"
    | "oeuvresDescriptionDateDeCreation"
    | "relatedAuthor"
  >,
  translatedPosition: string | undefined,
  lang: SupportedLocale,
) =>
  [
    artwork.relatedAuthor?.[0]?.artistesAuteursDescriptionPrenom,
    artwork.relatedAuthor?.[0]?.artistesAuteursDescriptionNom,
    artwork.oeuvresDescriptionTitre,
    artwork.oeuvresDescriptionTechnique?.[lang],
    artwork.oeuvresDescriptionSupport?.[lang],
    artwork.oeuvresDescriptionDateDeCreation,
    translatedPosition,
  ]
    .filter(Boolean)
    .filter((s) => s != undefined)
    .map((s) => slugify(s))
    .join("-");

type ImageNamingParams =
  | {
      primaryType: "artwork";
      artwork: Artwork;
      translatedPosition?: string;
    }
  | {
      primaryType: "thumbnail";
      pageNameInDefaultLanguage: string; // English
      title?: string;
      instanceName: string;
    }
  | {
      primaryType: "CMSPages";
      translatedPageName: string;
      instanceName: string;
    }
  | {
      primaryType: "SectionImportedImage";
      sectionTitle: string;
    };

/***
 * Generates a name for an image intended to be used in the public front.
 *
 * Names are not necessarily unique, it's up to the upload routine to check for duplicates.
 *
 * @param params - The parameters for the image name generation.
 * @returns The generated image name.
 */
export const generateImageName = (params: ImageNamingParams) => {
  switch (params.primaryType) {
    case "artwork":
      return buildArtworkImageName(
        params.artwork,
        params.translatedPosition,
        getMainLanguage(),
      );
    case "thumbnail":
      return [
        params.instanceName,
        params.pageNameInDefaultLanguage,
        params.title,
      ]
        .filter(Boolean)
        .filter((s) => s != undefined)
        .map((s) => slugify(s))
        .join("-");
    case "CMSPages":
      return [params.instanceName, params.translatedPageName]
        .map((s) => slugify(s))
        .join("-");
    case "SectionImportedImage":
      return [params.sectionTitle, "imported"].map((s) => slugify(s)).join("-");
  }
};

export function colorGradient(
  fadeFraction: number,
  hexColor1: string,
  hexColor2: string,
  hexColor3?: string,
) {
  const hexToRgb = (
    hex: string,
  ): { red: number; green: number; blue: number } => {
    let cleaned = hex.replace(/^#/, "");

    if (cleaned.length === 3) {
      cleaned = cleaned
        .split("")
        .map((c) => c + c)
        .join("");
    }

    const num = parseInt(cleaned, 16);
    return {
      red: (num >> 16) & 255,
      green: (num >> 8) & 255,
      blue: num & 255,
    };
  };

  let color1 = hexToRgb(hexColor1);
  let color2 = hexToRgb(hexColor2);
  let fade = fadeFraction;

  if (hexColor3) {
    fade = fade * 2;

    if (fade >= 1) {
      fade -= 1;
      color1 = hexToRgb(hexColor2);
      color2 = hexToRgb(hexColor3);
    }
  }

  const diffRed = color2.red - color1.red;
  const diffGreen = color2.green - color1.green;
  const diffBlue = color2.blue - color1.blue;

  const gradient = {
    red: Math.floor(color1.red + diffRed * fade),
    green: Math.floor(color1.green + diffGreen * fade),
    blue: Math.floor(color1.blue + diffBlue * fade),
  };

  const toHex = (value: number) => value.toString(16).padStart(2, "0");

  return `#${toHex(gradient.red)}${toHex(gradient.green)}${toHex(gradient.blue)}`;
}

export const tabMenuKeyToTranslationKey = (key: TabMenuItems) => {
  // Special cases for typos...
  switch (key) {
    case "author":
      return "dashboardTitleArtistes";
    case "audiovisual":
      return "dashboardTitleAudiovisuels";
    case "photography":
      return "dashboardTitlePhotos";
    case "writing":
      return "dashboardTitleWrittings";
  }

  const plural = key.endsWith("s")
    ? key
    : key.endsWith("y")
      ? key.slice(0, -1) + "ies"
      : key + "s";
  return `dashboardTitle${capitalizeFirstLetter(plural)}`;
};

/** Returns the exportable table key when pathname is a dashboard table route, else null. */
export const exportTableFromPath = (pathname: string): TabMenuItems | null => {
  const segments = pathname.split("/").filter(Boolean);
  if (
    segments[0] === "dashboard" &&
    tabMenuItems.includes(segments[1] as TabMenuItems)
  ) {
    return segments[1] as TabMenuItems;
  }
  return null;
};

/** Resolves a valid export table from a previous location, defaulting to artwork. */
export const resolveExportTable = (prevLocation?: string): TabMenuItems => {
  if (!prevLocation) return "artwork";
  const table = exportTableFromPath(prevLocation);
  return table ?? "artwork";
};

export const tabMenuKeyToPublicationStatusField = (key: TabMenuItems) => {
  const translationMapping = {
    artwork: "oeuvres",
    auction: "ventes",
    audiovisual: "audiovisuel",
    author: "artistesAuteurs",
    bibliography: "bibliographie",
    collector: "collectionneurs",
    document: "documents",
    exhibition: "expositions",
    multipleprint: "estampesMultiples",
    photography: "photos",
    writing: "ecrits",
  };

  return `${translationMapping[key]}PublicationStatus`;
};

export const isInSearch = (search: string, translatedLabel: string) => {
  return search.length > 0
    ? translatedLabel.toLowerCase().includes(search.toLowerCase())
    : true;
};

/** Parse JSON stored as string (handles double-encoded strings). */
export function tryParseJson<T>(v: unknown | undefined): T | undefined {
  if (v === undefined) {
    return undefined;
  }

  if (typeof v != "string") {
    return v as T;
  }

  try {
    const res = JSON.parse(v) as T;

    if (typeof res != "string") return res;

    return JSON.parse(res) as T;
  } catch {
    return undefined;
  }
}

/** Format label + value; French keeps a space before colon. */
export function formatTextWithColon(
  tradKey: string,
  language: SupportedLocale,
  value: string,
): string {
  if (language === "fr") {
    return `${tradKey} : ${value}`;
  }
  return `${tradKey}: ${value}`;
}

type TitleMultilingualInput = Pick<
  Artwork,
  "oeuvresDescriptionTitre" | "oeuvresDescriptionTitreMultilingual"
> & {
  oeuvresDescriptionTitreMultilingualPublished?: boolean | null;
};

export const getOeuvresDescriptionTitleInLanguageTranslationKey = (
  locale: string,
) => `artwork.form.titleInLanguage.${locale}`;

const getTrimmedTitle = (value?: string | null) =>
  typeof value === "string" ? value.trim() : "";

const isTitleMultilingualPublished = (input: TitleMultilingualInput) =>
  input.oeuvresDescriptionTitreMultilingualPublished !== false;

const formatTitleWithColon = (
  label: string,
  language: SupportedLocale,
  value: string,
) => (language === "fr" ? `${label} : ${value}` : `${label}: ${value}`);

const getTitleForLocale = (
  input: TitleMultilingualInput,
  locale: string,
  defaultLanguage: SupportedLocale,
): string => {
  if (locale === defaultLanguage) {
    return getTrimmedTitle(input.oeuvresDescriptionTitre);
  }
  const ml = input.oeuvresDescriptionTitreMultilingual as
    | Record<string, string>
    | null
    | undefined;
  return getTrimmedTitle(ml?.[locale]);
};

const getOrderedTitleLocales = (
  ml: Record<string, string> | null | undefined,
): string[] => {
  const extraLocales = Object.keys(ml ?? {})
    .filter((locale) => !(supportedLocales as readonly string[]).includes(locale))
    .sort();
  return [...supportedLocales, ...extraLocales];
};

/** Localized title: default locale uses the legacy title; secondary locales use their published multilingual value. */
export function resolveOeuvresDescriptionDisplayTitle(
  input: TitleMultilingualInput,
  language: SupportedLocale,
  defaultLanguage: SupportedLocale = getMainLanguage() as SupportedLocale,
): string {
  const legacy = getTrimmedTitle(input.oeuvresDescriptionTitre);
  if (language === defaultLanguage || !isTitleMultilingualPublished(input)) {
    return legacy;
  }

  const localized = getTitleForLocale(input, language, defaultLanguage);
  return localized || legacy;
}

export function getOeuvresDescriptionSecondaryTitleLines(
  input: TitleMultilingualInput,
  language: SupportedLocale,
  defaultLanguage: SupportedLocale = getMainLanguage() as SupportedLocale,
  t: (key: string) => string = (key) => key,
): string[] {
  if (!isTitleMultilingualPublished(input)) {
    return [];
  }

  const mainTitle = resolveOeuvresDescriptionDisplayTitle(
    input,
    language,
    defaultLanguage,
  );
  const ml = input.oeuvresDescriptionTitreMultilingual;
  const lines: string[] = [];

  for (const locale of getOrderedTitleLocales(ml)) {
    if (locale === language) continue;

    const title = getTitleForLocale(input, locale, defaultLanguage);
    if (!title || title === mainTitle) continue;

    lines.push(
      formatTitleWithColon(
        t(getOeuvresDescriptionTitleInLanguageTranslationKey(locale)),
        language,
        title,
      ),
    );
  }

  return lines;
}
